import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { EnhancedFAQs } from "@/components/seo/EnhancedFAQs";
import { AuthorBio } from "@/components/content/AuthorBio";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { RelatedResources } from "@/components/seo/RelatedResources";
import { BreadcrumbNavigation } from "@/components/seo/BreadcrumbNavigation";

const MIDWEST_STATES = [
  { name: 'Illinois', abbr: 'IL', url: 'illinois' },
  { name: 'Indiana', abbr: 'IN', url: 'indiana' },
  { name: 'Iowa', abbr: 'IA', url: 'iowa' },
  { name: 'Kansas', abbr: 'KS', url: 'kansas' },
  { name: 'Michigan', abbr: 'MI', url: 'michigan' },
  { name: 'Minnesota', abbr: 'MN', url: 'minnesota' },
  { name: 'Missouri', abbr: 'MO', url: 'missouri' },
  { name: 'Nebraska', abbr: 'NE', url: 'nebraska' },
  { name: 'North Dakota', abbr: 'ND', url: 'north-dakota' },
  { name: 'Ohio', abbr: 'OH', url: 'ohio' },
  { name: 'South Dakota', abbr: 'SD', url: 'south-dakota' },
  { name: 'Wisconsin', abbr: 'WI', url: 'wisconsin' }
];

const MIDWEST_FAQS = [
  {
    question: "How do Midwest PEOs handle manufacturing industry needs?",
    answer: "Midwest PEOs have deep manufacturing expertise, including complex shift scheduling, union relations, safety compliance, and workers' compensation management for industrial environments. They understand the unique challenges of manufacturing operations and seasonal workforce fluctuations common in the region."
  },
  {
    question: "Do Midwest PEOs understand agricultural business cycles?",
    answer: "Yes, many Midwest PEOs specialize in agricultural businesses and understand seasonal employment patterns, farm labor regulations, and the unique insurance needs of agricultural operations. They provide flexible HR solutions that adapt to harvest seasons and agricultural cycles."
  },
  {
    question: "How do PEOs help with the Midwest's skilled labor shortage?",
    answer: "Midwest PEOs offer competitive benefits packages and recruitment strategies that help attract skilled workers to manufacturing and technology roles. They understand regional talent markets and provide training and development programs to build the skilled workforce Midwest companies need."
  },
  {
    question: "What advantages do Midwest PEOs offer for business costs?",
    answer: "Midwest PEOs leverage the region's generally lower cost structure while providing access to Fortune 500-level benefits and services. They help businesses maintain competitive advantages through efficient operations and cost-effective HR solutions that reflect Midwest business values."
  }
];

const RELATED_RESOURCES = [
  {
    title: "Illinois PEO Services",
    description: "Complete guide to PEO services in Illinois",
    url: "/states/illinois",
    type: "state-guide" as const
  },
  {
    title: "Ohio PEO Providers",
    description: "Find the best PEO companies in Ohio",
    url: "/states/ohio",
    type: "state-guide" as const
  },
  {
    title: "Manufacturing Industry PEOs",
    description: "Specialized PEO services for manufacturing companies",
    url: "/industries/manufacturing",
    type: "industry-guide" as const
  },
  {
    title: "Professional Services PEOs",
    description: "PEO solutions for professional service firms",
    url: "/industries/professional-services",
    type: "industry-guide" as const
  }
];

export const MidwestRegion = () => {
  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title="Midwest PEO Services: Best Professional Employer Organizations in the Midwest"
        description="Find top PEO companies serving the Midwest region. Expert comparison of Professional Employer Organizations in IL, OH, MI, IN, WI, and all Midwest states. Get matched with the perfect PEO for your business."
        keywords="Midwest PEO, Illinois PEO, Ohio PEO, Michigan PEO, Indiana PEO, Wisconsin PEO, Midwest Professional Employer Organization"
        canonical="https://thepeoapp.com/regions/midwest"
      />
      
      <LocalBusinessSchema
        businessName="The PEO App - Midwest Region"
        serviceType="Professional Employer Organization Services"
        areaServed="Midwest United States"
        description="Professional Employer Organization matching services for businesses in the Midwest region"
        url="https://thepeoapp.com/regions/midwest"
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation 
            items={[
              { label: "Home", href: "/" },
              { label: "States", href: "/states" },
              { label: "Midwest Region", href: "/regions/midwest" }
            ]}
          />
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Midwest PEO Services: Professional Employer Organizations
            </h1>
            
            <div className="prose prose-lg max-w-none mb-12 text-muted-foreground">
              <p className="text-xl leading-relaxed">
                The Midwest is America's industrial heartland, home to manufacturing excellence, agricultural innovation, and growing technology sectors. Our vetted Professional Employer Organizations understand Midwest work ethic and provide reliable HR solutions for the region's diverse industries.
              </p>
            </div>

            <div className="bg-card p-8 rounded-xl shadow-lg mb-12">
              <h2 className="text-2xl font-bold mb-6">Why Choose a Midwest PEO?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Industry Expertise</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Deep manufacturing and industrial knowledge</li>
                    <li>• Agricultural business cycle understanding</li>
                    <li>• Union relations and collective bargaining</li>
                    <li>• Supply chain and logistics expertise</li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Regional Values</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Strong work ethic and reliability</li>
                    <li>• Cost-effective business solutions</li>
                    <li>• Long-term relationship focus</li>
                    <li>• Practical, no-nonsense approach</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-6">Midwest States We Serve</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {MIDWEST_STATES.map((state) => (
                  <Link
                    key={state.abbr}
                    to={`/states/${state.url}`}
                    className="bg-background p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-2 border"
                  >
                    <span className="text-primary font-semibold">{state.abbr}</span>
                    <span className="text-foreground">{state.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-center mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready to Find Your Midwest PEO?</h2>
              <p className="text-muted-foreground mb-6">
                Get matched with PEO providers who understand Midwest business values
              </p>
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free PEO Quotes
                </Button>
              </Link>
            </div>

            <RelatedResources 
              title="Related Midwest Resources"
              resources={RELATED_RESOURCES}
            />

            <div className="mt-16">
              <EnhancedFAQs 
                title="Midwest PEO FAQ"
                faqs={MIDWEST_FAQS}
                showSchema={true}
              />
            </div>

            <div className="mt-16">
              <AuthorBio showFullBio={false} />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};